import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  CELSIUS_ID,
  FAHRENHEIT_ID, SPEED_FEET, SPEED_METERS,
} from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelValueRender';
import PaginatedListNavigation
  from '../../../paginatedlist/PaginatedListNavigation/PaginatedListNavigation';
import {
  ALERT_LIST_COLUMNS,
  COLUMN_ORDER,
  buildColumnVisibility,
  toDisabledColumns,
} from './alertListColumns';
import { filterLabel } from './useAlertsListWidgetData';
import AlertRow from './AlertRow';
import ControlledSortHeader from '../../../common/SortableTableHeader/ControlledSortHeader';
import { getPublicDashboardToken } from '../../services/dashboardApi';

// Reverse lookup: display label → column id. Used to translate the label the
// cog menu hands back (because PaginatedListNavigation uses the map keys as
// checkbox labels) into the stable column id we persist in context.
const LABEL_TO_ID = Object.entries(ALERT_LIST_COLUMNS).reduce((acc, [id, cfg]) => {
  acc[cfg.label] = id;
  return acc;
}, {});

const AlertsListWidgetView = ({
  context, data, viewState, onContextChange, userPreferences,
}) => {
  const {
    disabledColumns, sortType, sort, rowsPerPage = 10,
  } = context;

  const { pageNo = 0, setPageNo = () => {} } = viewState;
  const isPublicPage = !!getPublicDashboardToken();

  // Ensure userPreferences has a default temperatureScale
  const effectiveUserPreferences = useMemo(() => ({
    temperatureScale: CELSIUS_ID,
    ...userPreferences,
  }), [userPreferences]);

  // Internal id→bool visibility, driven by disabledColumns.
  const columnVisibility = useMemo(
    () => buildColumnVisibility(disabledColumns),
    [disabledColumns],
  );

  // Label-keyed map for the cog menu.
  const columnVisibilityByLabel = useMemo(
    () => COLUMN_ORDER.reduce((acc, id) => {
      acc[ALERT_LIST_COLUMNS[id].label] = columnVisibility[id];
      return acc;
    }, {}),
    [columnVisibility],
  );

  const handleToggleColumn = (label, visible) => {
    const columnId = LABEL_TO_ID[label];
    if (!columnId) return;
    if (!visible && ALERT_LIST_COLUMNS[columnId].required) return;

    const nextVisibility = { ...columnVisibility, [columnId]: visible };
    onContextChange({
      ...context,
      disabledColumns: toDisabledColumns(nextVisibility),
    });
  };

  const handlePrevPage = () => setPageNo(Math.max(0, pageNo - 1));
  const handleNextPage = () => setPageNo(pageNo + 1);

  const handleSetRowsPerPage = (n) => onContextChange({ ...context, rowsPerPage: n });

  const handleSort = (nextSort, nextSortType) => onContextChange({
    ...context,
    sort: nextSort,
    sortType: nextSortType,
  });

  const isVisible = useCallback(
    (columnId) => columnVisibility[columnId],
    [columnVisibility],
  );

  if (!data || !data.alerts || data.alerts.length === 0) {
    return (
      <div className="p-4 text-center text-muted">
        <div className="mb-2">No alerts match your current filters.</div>
        <small>
          {`Alerts will appear here automatically when they meet this criteria: ${filterLabel(context)}.`}
        </small>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column h-100 w-100">
      <div className="d-flex px-2 pt-1">
        <div className="ml-auto">
          <PaginatedListNavigation
            currentPage={pageNo}
            totalResults={data.totalResults}
            onPrevPage={handlePrevPage}
            onNextPage={handleNextPage}
            columnVisibility={columnVisibilityByLabel}
            onToggleColumn={isPublicPage ? () => {} : handleToggleColumn}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={isPublicPage ? () => {} : handleSetRowsPerPage}
            showSettings={!isPublicPage}
          />
        </div>
      </div>
      <div className="flex-grow-1" style={{ overflowY: 'auto' }}>
        <table className="table table-sm table-striped mb-0">
          <thead>
            <tr>
              {COLUMN_ORDER.filter(isVisible).map((columnId) => {
                const col = ALERT_LIST_COLUMNS[columnId];
                if (col.sortType == null || isPublicPage) {
                  return <th key={columnId}>{col.label}</th>;
                }
                const selected = col.sortType === sortType;
                return (
                  <ControlledSortHeader
                    key={columnId}
                    label={col.label}
                    isSelected={selected}
                    direction={selected ? sort : undefined}
                    onSort={(nextSort) => handleSort(nextSort, col.sortType)}
                  />
                );
              })}
            </tr>
          </thead>
          <tbody>
            {data.alerts.map((alert) => {
              const rawKey = alert.channelKey && alert.channelKey.channelKey;
              const samples = (data.samplesByChannelKey && rawKey)
                ? data.samplesByChannelKey[rawKey]
                : null;
              return (
                <AlertRow
                  key={alert.id}
                  alert={alert}
                  isVisible={isVisible}
                  trendSamples={samples}
                  trendsLoading={!!data.trendsLoading}
                  userPreferences={effectiveUserPreferences}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

AlertsListWidgetView.propTypes = {
  context: PropTypes.shape({
    filter: PropTypes.string,
    savedSearchId: PropTypes.number,
    disabledColumns: PropTypes.arrayOf(PropTypes.string),
    rowsPerPage: PropTypes.number,
    sortType: PropTypes.number,
    sort: PropTypes.oneOf(['ASC', 'DES']),
  }).isRequired,
  data: PropTypes.shape({
    alerts: PropTypes.arrayOf(PropTypes.object),
    totalResults: PropTypes.number,
    // eslint-disable-next-line react/forbid-prop-types
    samplesByChannelKey: PropTypes.object,
    trendsLoading: PropTypes.bool,
    trendsError: PropTypes.bool,
  }).isRequired,
  viewState: PropTypes.shape({
    pageNo: PropTypes.number,
    setPageNo: PropTypes.func,
  }),
  onContextChange: PropTypes.func.isRequired,
  userPreferences: PropTypes.shape({
    temperatureScale: PropTypes.oneOf([CELSIUS_ID, FAHRENHEIT_ID]),
    airSpeedScale: PropTypes.oneOf([SPEED_METERS, SPEED_FEET]),
  }).isRequired,
};

AlertsListWidgetView.defaultProps = {
  viewState: {},
};

export default AlertsListWidgetView;
